<template>
  <div class="results-wrap">
    <div class="results-bg"></div>

    <!-- ══ RESULTS SCREEN ══ -->
    <template v-if="!showFinal">
      <div class="slide-scroll">
        <div class="slide-inner">
          <h1 class="main-title">Оцените эффективность Вашей стратегии на {{ assetDisplayName }}</h1>
          <p class="sub-title">Где ваш результат лучше? Почему?</p>

          <div class="promo-box">
            <p class="promo-label">Заработанная прибыль / убыток</p>
            <div class="cards-row">
              <div class="result-card" v-for="lv in levels" :key="lv.num" :style="{ '--result-card-color': lv.color }">
                <div class="result-card-row">
                  <div class="icon-wrapper">
                    <img :src="lv.icon" class="card-icon" :class="{ 'card-icon-round': lv.num === 1 }" alt="">
                  </div>
                  <div class="result-card-name">{{ lv.name }}</div>
                  <span class="result-card-profit">{{ lv.profit >= 0 ? '+' : '-' }}${{ Math.abs(lv.profit).toLocaleString(undefined, { maximumFractionDigits: 0 }) }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="chart-box" v-if="combinedChartData">
            <p class="chart-label">График прибыли (в $ млн)</p>
            <div class="chart-html-legend">
              <div v-for="lv in levels" :key="lv.num" class="chart-html-legend-item">
                <span class="chart-html-legend-swatch" :style="{ background: lv.color }"></span>
                <span>{{ lv.name }}</span>
              </div>
            </div>
            <div class="chart-area">
              <Line :data="combinedChartData" :options="combinedChartOptions" :plugins="legendGapPlugins" :style="{ position: 'relative', height: '100%', width: '100%' }"/>
            </div>
          </div>

          <div class="promo-box">
            <p class="promo-label">Доходность</p>
            <div class="cards-row">
              <div class="result-card" v-for="lv in levels" :key="lv.num" :style="{ '--result-card-color': lv.color }">
                <div class="result-card-row">
                  <div class="icon-wrapper">
                    <img :src="lv.icon" class="card-icon" :class="{ 'card-icon-round': lv.num === 1 }" alt="">
                  </div>
                  <div class="result-card-name">{{ lv.name }}</div>
                  <span class="result-card-profit">{{ lv.profitability >= 0 ? '+' : '' }}{{ lv.profitability.toLocaleString('ru-RU') }}%</span>
                </div>
              </div>
            </div>
          </div>

          <div class="chart-box" v-if="profitabilityChartData">
            <p class="chart-label">График доходности (%)</p>
            <div class="chart-html-legend">
              <div v-for="lv in levels" :key="lv.num" class="chart-html-legend-item">
                <span class="chart-html-legend-swatch" :style="{ background: lv.color }"></span>
                <span>{{ lv.name }}</span>
              </div>
            </div>
            <div class="chart-area">
              <Line :data="profitabilityChartData" :options="profitabilityChartOptions" :plugins="legendGapPlugins" :style="{ position: 'relative', height: '100%', width: '100%' }"/>
            </div>
          </div>

          <div class="promo-box">
            <p class="promo-label">Средний риск на сделку</p>
            <div class="cards-row">
              <div class="result-card" v-for="lv in levels" :key="lv.num" :style="{ '--result-card-color': lv.color }">
                <div class="result-card-row">
                  <div class="icon-wrapper">
                    <img :src="lv.icon" class="card-icon" :class="{ 'card-icon-round': lv.num === 1 }" alt="">
                  </div>
                  <div class="result-card-name">{{ lv.name }}</div>
                  <span class="result-card-profit">{{ lv.risk.toLocaleString('ru-RU') }}%</span>
                </div>
              </div>
            </div>
          </div>

          <div class="chart-box" v-if="riskChartData">
            <p class="chart-label">График рисков (%)</p>
            <div class="chart-html-legend">
              <div v-for="lv in levels" :key="lv.num" class="chart-html-legend-item">
                <span class="chart-html-legend-swatch" :style="{ background: lv.color }"></span>
                <span>{{ lv.name }}</span>
              </div>
            </div>
            <div class="chart-area">
              <Line :data="riskChartData" :options="riskChartOptions" :plugins="legendGapPlugins" :style="{ position: 'relative', height: '100%', width: '100%' }"/>
            </div>
          </div>
        </div>
      </div>

      <div class="slide-footer">
        <button class="nav-btn" @click="goBack">Меню</button>
        <button class="nav-btn" @click="showFinal = true">Далее</button>
      </div>
    </template>

    <!-- ══ FINAL SCREEN ══ -->
    <template v-else>
      <div class="slide-scroll">
        <div class="slide-inner final-inner">
          <h1 class="main-title">Стратегия - ключ к рациональным инвестиционным решениям</h1>

          <div class="promo-box">
            <p class="promo-label">Стратегия задает правила поведения на рынке</p>
            <div class="cards-row">
              <div class="strategy-card strategy-card-blue">Как распределять капитал?</div>
              <div class="strategy-card strategy-card-green">Когда фиксировать прибыль или рисковать?</div>
              <div class="strategy-card strategy-card-gray">Как определять объем позиции?</div>
            </div>
          </div>

          <div class="two-col">
            <div class="two-col-card">
              <p class="two-col-title">Проблема инвесторов</p>
              <div class="problem-items-col">
                <div class="problem-item-card">Рано фиксируют прибыль</div>
                <div class="problem-item-card">Держат убыточные позиции</div>
              </div>
              <img src="@/assets/icons/example-chart-1.png" class="problem-chart-img" alt="">
            </div>
            <div class="two-col-card">
              <p class="two-col-title">Задача инвестора</p>
              <div class="problem-items-col">
                <div class="problem-item-card">Выигрывать в долгосрочном периоде</div>
                <div class="problem-item-card">Стратегия помогает переживать кризисы и не поддаваться эмоциям</div>
              </div>
              <img src="@/assets/icons/example-chart-2.png" class="problem-chart-img" alt="">
            </div>
          </div>
        </div>
      </div>

      <div class="slide-footer">
        <button class="nav-btn" @click="goBack">Меню</button>
        <button class="nav-btn" @click="goHome">Далее</button>
      </div>
    </template>

    <div class="exit-overlay" v-if="showExitMenu" @click.self="showExitMenu = false">
      <div class="exit-modal">
        <h3 class="exit-title">Меню</h3>
        <button class="exit-opt-btn" @click="showExitMenu = false">Продолжить просмотр</button>
        <button class="exit-opt-btn" v-if="!isSimulatorMode" @click="restartCases">Пройти кейсы заново</button>
        <button class="exit-opt-btn" v-if="isSimulatorMode" @click="goHome">Начать новую игру</button>
        <button class="exit-opt-btn" @click="goToModeSelect">Выбор режима</button>
        <button class="exit-opt-btn" @click="goHome">Выбор актива</button>
        <button class="exit-opt-btn" @click="goToInstructions">Инструкция</button>
      </div>
    </div>

  </div>
</template>

<script>
import { markRaw } from 'vue'
import { Line } from 'vue-chartjs'
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  PointElement, LineElement, Tooltip, Legend, Title
} from 'chart.js'
import chartImg     from '@/assets/icons/chart.png'
import zeroImg      from '@/assets/icons/zero.png'
import chartDownImg from '@/assets/icons/chart-down.png'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Title)

const LEVEL_CONFIG = [
  { num: 1, name: 'Играем на прибыль', color: 'var(--color-accent)',      icon: chartImg,     startBalance: 6000000 },
  { num: 2, name: 'Играем с нуля',     color: 'var(--color-market-blue)', icon: zeroImg,      startBalance: 3000000 },
  { num: 3, name: 'Играем с убытков',  color: 'var(--color-market-gray)', icon: chartDownImg, startBalance: 1500000 }
]

const SIMULATOR_LEVEL_CONFIG = [
  { num: 4, name: 'Режим симулятора', color: 'var(--color-accent)', icon: chartImg, startBalance: 3000000 }
]

const ASSET_NAMES = { SPX: 'S&P 500', Gold: 'Золоте', EURUSD: 'EUR/USD' }

export default {
  name: 'ResultsView',
  components: { Line },

  data() {
    return {
      totalSum: { 1: 0, 2: 0, 3: 0 },
      historyPoints: { 1: [], 2: [], 3: [] },
      selectedAsset: null,
      combinedChartData: null,
      profitabilityChartData: null,
      riskChartData: null,
      showFinal: false,
      showExitMenu: false,
      isSimulatorMode: false,
      isMobile: typeof window !== 'undefined' && window.innerWidth <= 700,
      legendGapPlugins: [{
        id: 'legendGap',
        beforeInit(chart) {
          const origFit = chart.legend.fit.bind(chart.legend)
          chart.legend.fit = function () {
            origFit()
            this.height += 20
          }
        }
      }]
    }
  },

  computed: {
    assetDisplayName() {
      return ASSET_NAMES[this.selectedAsset] || this.selectedAsset || ''
    },
    levels() {
      const config = this.isSimulatorMode ? SIMULATOR_LEVEL_CONFIG : LEVEL_CONFIG
      return config.map(lv => ({
        ...lv,
        total:  this.totalSum[lv.num] || lv.startBalance,
        profit: (this.totalSum[lv.num] || lv.startBalance) - lv.startBalance,
        profitability: +(((this.totalSum[lv.num] || lv.startBalance) - lv.startBalance) / lv.startBalance * 100).toFixed(1),
        risk: (() => {
          const pts = this.historyPoints[lv.num]
          if (!pts || !pts[0] || !pts[0].data || pts[0].data.length < 2) return 0
          const raw = pts[0].data.map(p => p.y)
          const changes = raw.slice(1).map((v, i) => Math.abs((v - raw[i]) / raw[i]) * 100)
          return +(changes.reduce((a, b) => a + b, 0) / changes.length).toFixed(2)
        })()
      }))
    },
    combinedChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        layout: { padding: { top: 8, bottom: 8, left: 4, right: 8 } },
        plugins: {
          legend: { display: !this.isMobile, position: 'top', align: 'start', labels: { color: this.cssVar('--color-navy', '#002060'), font: { size: this.fsPx(18, 13) }, padding: 8, boxWidth: 28, boxHeight: 1 } },
          tooltip: {
            callbacks: {
              label: ctx => ctx.parsed.y != null
                ? ctx.dataset.label + ': ' + (ctx.parsed.y >= 0 ? '+' : '') + '$' + ctx.parsed.y.toLocaleString(undefined, { maximumFractionDigits: 3 }) + ' млн'
                : ''
            }
          }
        },
        scales: {
          x: { display: true, ticks: { maxTicksLimit: 8, color: this.cssVar('--color-axis', '#999'), font: { size: this.fsPx(18, 13) }, maxRotation: 0 }, grid: { color: this.cssVar('--color-border-subtle', '#eee') } },
          y: { ticks: { color: this.cssVar('--color-axis', '#999'), font: { size: this.fsPx(18, 13) } }, grid: { color: this.cssVar('--color-border-subtle', '#eee') } }
        }
      }
    },
    riskChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        layout: { padding: { top: 8, bottom: 8, left: 4, right: 8 } },
        plugins: {
          legend: { display: !this.isMobile, position: 'top', align: 'start', labels: { color: this.cssVar('--color-navy', '#002060'), font: { size: this.fsPx(18, 13) }, padding: 8, boxWidth: 28, boxHeight: 1 } },
          tooltip: {
            callbacks: {
              label: ctx => ctx.parsed.y != null
                ? ctx.dataset.label + ': ' + ctx.parsed.y.toLocaleString(undefined, { maximumFractionDigits: 2 }) + '%'
                : ''
            }
          }
        },
        scales: {
          x: { display: true, ticks: { maxTicksLimit: 8, color: this.cssVar('--color-axis', '#999'), font: { size: this.fsPx(18, 13) }, maxRotation: 0 }, grid: { color: this.cssVar('--color-border-subtle', '#eee') } },
          y: { min: 0, ticks: { color: this.cssVar('--color-axis', '#999'), font: { size: this.fsPx(18, 13) }, callback: v => v + '%' }, grid: { color: this.cssVar('--color-border-subtle', '#eee') } }
        }
      }
    },
    profitabilityChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        layout: { padding: { top: 8, bottom: 8, left: 4, right: 8 } },
        plugins: {
          legend: { display: !this.isMobile, position: 'top', align: 'start', labels: { color: this.cssVar('--color-navy', '#002060'), font: { size: this.fsPx(18, 13) }, padding: 8, boxWidth: 28, boxHeight: 1 } },
          tooltip: {
            callbacks: {
              label: ctx => ctx.parsed.y != null
                ? ctx.dataset.label + ': ' + ctx.parsed.y.toLocaleString(undefined, { maximumFractionDigits: 2 }) + '%'
                : ''
            }
          }
        },
        scales: {
          x: { display: true, ticks: { maxTicksLimit: 8, color: this.cssVar('--color-axis', '#999'), font: { size: this.fsPx(18, 13) }, maxRotation: 0 }, grid: { color: this.cssVar('--color-border-subtle', '#eee') } },
          y: { ticks: { color: this.cssVar('--color-axis', '#999'), font: { size: this.fsPx(18, 13) }, callback: v => v + '%' }, grid: { color: this.cssVar('--color-border-subtle', '#eee') } }
        }
      }
    }
  },

  methods: {
    goBack() {
      this.isSimulatorMode = localStorage.getItem('gameMode') === 'simulator'
      this.showExitMenu = true
    },
    restartCases() {
      const asset = this.selectedAsset || localStorage.getItem('selectedAsset')
      localStorage.clear()
      if (asset) localStorage.setItem('selectedAsset', asset)
      localStorage.setItem('gameMode', 'cases')
      localStorage.setItem('level', '1')
      this.showExitMenu = false
      this.$router.push('/cases')
    },
    goToModeSelect() {
      const asset = this.selectedAsset || localStorage.getItem('selectedAsset')
      localStorage.clear()
      if (asset) localStorage.setItem('selectedAsset', asset)
      localStorage.setItem('level', '0')
      localStorage.setItem('levelEnded', 'true')
      this.showExitMenu = false
      this.$router.push('/mode')
    },
    goToInstructions() {
      this.showExitMenu = false
      this.$router.push('/instructions?back=1')
    },
    goHome() {
      localStorage.clear()
      this.$router.push('/')
    },
    fsPx(n, mobile = n) {
      if (typeof window === 'undefined') return n
      if (window.innerWidth <= 700) return mobile
      return Math.min(n * window.innerWidth / 1280, n * window.innerHeight / 800)
    },
    cssVar(name, fallback) {
      const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
      return value || fallback
    },

    buildChart() {
      const datasets = []
      const datasetColors = [
        this.cssVar('--color-accent', '#AAC56F'),
        this.cssVar('--color-market-blue', '#64A9DC'),
        this.cssVar('--color-market-gray', '#B9BDC7')
      ]
      const config = this.isSimulatorMode ? SIMULATOR_LEVEL_CONFIG : LEVEL_CONFIG
      for (let i = 0; i < config.length; i++) {
        const lv = config[i]
        const pts = this.historyPoints[lv.num]
        if (!pts || !pts[0] || !pts[0].data || pts[0].data.length < 2) continue
        const raw1 = pts[0].data.map(p => p.y)
        const data = raw1.map(v => +((v - lv.startBalance) / 1000000).toFixed(3))
        const ptColors = raw1.map((v, i) => i === 0 ? 'transparent' : v >= raw1[i - 1] ? '#66BB6A' : '#EF5350')
        datasets.push({
          label: lv.name,
          data,
          borderColor: datasetColors[i],
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: ptColors.map(c => c === 'transparent' ? 0 : 5),
          pointBackgroundColor: ptColors,
          pointBorderWidth: 0,
          clip: false,
          tension: 0.1
        })
      }
      if (!datasets.length) return
      const maxLen = Math.max(...datasets.map(d => d.data.length))
      this.combinedChartData = markRaw({
        labels: Array.from({ length: maxLen }, (_, i) => i),
        datasets
      })
    },

    buildProfitabilityChart() {
      const datasets = []
      const datasetColors = [
        this.cssVar('--color-accent', '#AAC56F'),
        this.cssVar('--color-market-blue', '#64A9DC'),
        this.cssVar('--color-market-gray', '#B9BDC7')
      ]
      const config = this.isSimulatorMode ? SIMULATOR_LEVEL_CONFIG : LEVEL_CONFIG
      for (let i = 0; i < config.length; i++) {
        const lv = config[i]
        const pts = this.historyPoints[lv.num]
        if (!pts || !pts[0] || !pts[0].data || pts[0].data.length < 2) continue
        const raw2 = pts[0].data.map(p => p.y)
        const data = raw2.map(v => +((v - lv.startBalance) / lv.startBalance * 100).toFixed(2))
        const ptColors = raw2.map((v, i) => i === 0 ? 'transparent' : v >= raw2[i - 1] ? '#66BB6A' : '#EF5350')
        datasets.push({
          label: lv.name,
          data,
          borderColor: datasetColors[i],
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: ptColors.map(c => c === 'transparent' ? 0 : 5),
          pointBackgroundColor: ptColors,
          pointBorderWidth: 0,
          clip: false,
          tension: 0.1
        })
      }
      if (!datasets.length) return
      const maxLen = Math.max(...datasets.map(d => d.data.length))
      this.profitabilityChartData = markRaw({
        labels: Array.from({ length: maxLen }, (_, i) => i),
        datasets
      })
    },

    buildRiskChart() {
      const datasets = []
      const datasetColors = [
        this.cssVar('--color-accent', '#AAC56F'),
        this.cssVar('--color-market-blue', '#64A9DC'),
        this.cssVar('--color-market-gray', '#B9BDC7')
      ]
      const config = this.isSimulatorMode ? SIMULATOR_LEVEL_CONFIG : LEVEL_CONFIG
      for (let i = 0; i < config.length; i++) {
        const lv = config[i]
        const pts = this.historyPoints[lv.num]
        if (!pts || !pts[0] || !pts[0].data || pts[0].data.length < 2) continue
        const raw = pts[0].data.map(p => p.y)
        const data = raw.map((v, i) => i === 0 ? 0 : +(Math.abs((v - raw[i - 1]) / raw[i - 1]) * 100).toFixed(2))
        const ptColors = raw.map((v, i) => i === 0 ? 'transparent' : v >= raw[i - 1] ? '#66BB6A' : '#EF5350')
        datasets.push({
          label: lv.name,
          data,
          borderColor: datasetColors[i],
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: ptColors.map(c => c === 'transparent' ? 0 : 5),
          pointBackgroundColor: ptColors,
          pointBorderWidth: 0,
          clip: false,
          tension: 0.1
        })
      }
      if (!datasets.length) return
      const maxLen = Math.max(...datasets.map(d => d.data.length))
      this.riskChartData = markRaw({
        labels: Array.from({ length: maxLen }, (_, i) => i),
        datasets
      })
    }
  },

  mounted() {
    if (localStorage.totalSum)      this.totalSum      = JSON.parse(localStorage.totalSum)
    if (localStorage.historyPoints) this.historyPoints = JSON.parse(localStorage.historyPoints)
    if (localStorage.selectedAsset) this.selectedAsset = localStorage.selectedAsset
    this.isSimulatorMode = localStorage.getItem('gameMode') === 'simulator'
    localStorage.setItem('hasResult', 'true')
    this.buildChart()
    this.buildProfitabilityChart()
    this.buildRiskChart()
  }
}
</script>

<style scoped>
.results-wrap {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.results-bg {
  position: absolute;
  inset: 0;
  background: url('../assets/icons/bg2.png') center / cover no-repeat;
  z-index: 0;
  pointer-events: none;
}

.slide-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  z-index: 1;
}

.slide-inner {
  padding: var(--space-12) var(--space-65) var(--space-20);
  display: flex;
  flex-direction: column;
  gap: var(--space-14);
}

.main-title {
  font-size: var(--fs-32);
  font-weight: 700;
  color: var(--color-navy);
  line-height: 1.2;
  margin: var(--space-4) 0 0;
}

.sub-title {
  font-size: var(--fs-25);
  color: var(--color-muted);
  margin: 0 0 var(--space-32) 0;
}

.promo-box {
  background: var(--surface-glass);
  border-radius: var(--space-10);
  padding: var(--space-10) var(--space-18) var(--space-18);
  box-shadow: var(--shadow-soft);
}

.promo-label {
  font-size: var(--fs-25);
  font-weight: 700;
  color: var(--color-navy);
  margin: 0 0 var(--space-10);
}

.cards-row {
  display: flex;
  gap: var(--space-10);
}

/* ── Results cards ── */
.result-card {
  flex: 1;
  background: var(--result-card-color);
  border-radius: var(--space-8);
  padding: var(--space-10);
  color: var(--color-white);
}

.result-card-row {
  display: flex;
  align-items: center;
  min-width: 0;
}

.icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--color-white-border);
  border-radius: var(--space-8);
  background: transparent;
  padding: var(--space-2) var(--space-3);
  flex-shrink: 0;
}

.card-icon { width: var(--space-24); height: var(--space-24); }
.card-icon-round { border-radius: var(--space-4); }

.result-card-name {
  flex: 1;
  min-width: 0;
  height: var(--space-28);
  font-size: var(--fs-16);
  font-weight: 700;
  color: var(--color-white);
  display: flex;
  align-items: center;
  padding-left: var(--space-8);
  background: var(--gradient-result-title);
}

.result-card-profit {
  font-size: var(--fs-28);
  font-weight: 700;
  color: var(--color-white);
  white-space: nowrap;
  padding-left: var(--space-14);
  text-align: right;
}

/* ── Chart box ── */
.chart-box {
  background: var(--surface-glass);
  border-radius: var(--space-10);
  padding: var(--space-10) var(--space-18) var(--space-14);
  box-shadow: var(--shadow-soft);
  margin-bottom: var(--space-50);
}

.chart-label {
  font-size: var(--fs-23);
  font-weight: 700;
  color: var(--color-navy);
  margin: 0 0 var(--space-2);
}

.chart-area {
  height: var(--space-200);
  position: relative;
}

/* ── Strategy cards (final screen) ── */
.strategy-card {
  flex: 1;
  border-radius: var(--space-8);
  padding: var(--space-16) var(--space-14);
  color: var(--color-white);
  font-size: var(--fs-16);
  font-weight: 700;
  line-height: 1.3;
}
.strategy-card-blue { background: var(--color-market-blue); }
.strategy-card-green { background: var(--color-accent); }
.strategy-card-gray { background: var(--color-level-gray); }

/* ── Two-column section ── */
.two-col {
  display: flex;
  gap: var(--space-24);
}

.final-inner { gap: 0; }
.final-inner .main-title { margin-bottom: var(--space-20); }
.final-inner .promo-box  { margin-top: var(--space-32); margin-bottom: var(--space-14); }
.final-inner .two-col    { gap: var(--space-24); }

.two-col-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-10);
}

.two-col-title {
  font-size: var(--fs-25);
  font-weight: 700;
  color: var(--color-navy);
  margin: 0;
}

.problem-items-col {
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
}

.problem-item-card {
  width: 100%;
  padding: var(--space-14) var(--space-14);
  color: var(--color-navy);
  font-size: var(--fs-14);
  line-height: 1.3;
  box-shadow: var(--shadow-soft);
  background: var(--surface-glass);
  border-radius: var(--space-10);
}

.problem-chart-img {
  flex: 1;
  width: 100%;
  min-height: 0;
  object-fit: contain;
  object-position: left;
  border-radius: var(--space-6);
  margin-top: var(--space-14);
}

/* ── Footer ── */
.slide-footer {
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-6) var(--page-x) var(--space-16);
}


.nav-btn {
  background: var(--color-button);
  color: var(--color-navy);
  border: none;
  padding: 0 var(--space-28);
  height: var(--space-54);
  min-width: var(--space-200);
  font-size: var(--fs-20);
  font-weight: 600;
  border-radius: var(--space-6);
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--shadow-soft-large);
}
.nav-btn:hover { background: var(--color-button-hover); }

.exit-overlay {
  position: absolute;
  inset: 0;
  background: var(--overlay-exit);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  z-index: 100;
}

.exit-modal {
  background: var(--surface-exit);
  border-radius: var(--space-12);
  padding: var(--space-28) var(--space-32);
  width: var(--space-360);
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
  box-shadow: var(--shadow-exit);
}

.exit-title {
  font-size: var(--fs-20);
  font-weight: 700;
  color: var(--color-navy);
  margin: 0 0 var(--space-8);
  text-align: left;
}

.exit-opt-btn {
  background: var(--surface-exit-button);
  color: var(--color-navy);
  border: none;
  border-radius: var(--space-10);
  padding: var(--space-16) var(--space-20);
  font-size: var(--fs-16);
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  box-shadow: var(--shadow-exit-button);
}

.exit-opt-btn:hover { background: var(--surface-exit-button-hover); }

.chart-html-legend { display: none; }

@media (max-width: 700px) {
  .chart-html-legend {
    display: flex; flex-direction: column; gap: 5px; padding: 0 0 8px;
  }
  .chart-html-legend-item {
    display: flex; align-items: center; gap: 8px;
    font-size: 13px; color: var(--color-navy);
  }
  .chart-html-legend-swatch {
    display: inline-block; width: 28px; height: 2px;
    border-radius: 1px; flex-shrink: 0;
  }
}

@media (max-width: 700px) {
  .results-wrap { overflow-y: auto; }
  .slide-scroll { overflow-y: auto; }
  .slide-inner { padding: 14px 16px 10px; }
  .main-title { font-size: 22px; margin: 12px 0 8px; line-height: 1.16; }
  .sub-title { font-size: 16px; margin-bottom: 14px; }
  .promo-box, .chart-box { border-radius: 12px; padding: 12px; }
  .promo-label { font-size: 18px; }
  .chart-label { font-size: 18px; margin-bottom: 6px; }
  .cards-row { flex-direction: column; gap: 10px; }
  .result-card { min-height: auto; padding: 9px; }
  .result-card-name { height: auto; min-height: 26px; font-size: 15px; }
  .result-card-profit { font-size: 24px; }
  .chart-area { height: 180px; }
  .two-col { flex-direction: column; gap: 12px; }
  .final-inner {
    padding: 12px 14px 8px;
  }
  .final-inner .main-title {
    font-size: 21px;
    margin-bottom: 12px;
  }
  .final-inner .promo-box {
    margin-bottom: 12px;
    padding: 12px;
  }
  .final-inner .promo-label {
    font-size: 16px;
    margin-bottom: 10px;
  }
  .final-inner .cards-row {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
  }
  .strategy-card {
    font-size: 14px;
    padding: 10px 12px;
    line-height: 1.2;
  }
  .final-inner .two-col {
    gap: 10px;
    padding-left: 0;
  }
  .two-col-card {
    gap: 8px;
  }
  .two-col-title {
    font-size: 17px;
  }
  .problem-items-col {
    gap: 6px;
  }
  .problem-item-card {
    font-size: 13px;
    padding: 10px;
    line-height: 1.2;
  }
  .problem-chart-img {
    max-height: 150px;
    margin-top: 4px;
  }
  .slide-footer { padding: 6px 14px 10px; gap: 10px; }
  .nav-btn { min-width: 82px; height: 38px; padding: 0 10px; font-size: 14px; }
  .exit-modal {
    width: min(320px, 88vw);
    padding: 24px 18px;
  }
  .exit-opt-btn {
    padding: 14px 16px;
    font-size: 15px;
  }
}
</style>
